import {
  call,
  put,
  take,
  race,
  takeLatest,
  putResolve,
} from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import { loginApi, refreshAuth, session, logoutApi } from './login.api';
import {
  userLoggedIn,
  userLoginFail,
} from '../../../common/actions/auth.actions';
import { trackLogoutTrigger } from '../../../common/utils/logoutDiagnostics';
import {
  storageRoles,
  storageAuthInformation,
  XSRFToken,
  tenantId,
} from '../../../common/constants/strings';
import {
  loginActions,
  logoutActions,
  session as sessionStart,
} from './login.actions';

import { TokenDto, LoginDto } from './login.dtos';
import { AuthDto, authInit } from '../../../common/dtos/auth';
//import { Session } from 'inspector';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getCookie = (): string => {
  var cookie = document.cookie
    .split(';')
    .find((strCookie) => strCookie.indexOf(XSRFToken) >= 0);
  if (cookie != null) {
    return cookie.trim().substring(XSRFToken.length + 1);
  }
  return '';
};

function* authorize(creds: LoginDto) {
  try {
    const token: TokenDto = yield call(loginApi, creds);
    if (token !== undefined && token.isPasswordExpired) {
      yield putResolve({
        type: getType(loginActions.success),
        payload: token,
      });
      yield put({
        type: getType(userLoginFail),
        payload: 'Password Update Required',
      });
      return token;
    } else if (token.authToken && token.authToken.length > 0) {
      // Cookie-based auth only. No token storage in JS.
      // Note: This flow is for Google Authenticator MFA (legacy), which still returns TokenDto.
      // Tokens are stored in HttpOnly cookies by the backend, not in sessionStorage.
      const auth: AuthDto = {
        status: 'authenticated',  // Login succeeded
        authToken: '',  // Empty - authentication via HttpOnly cookie only
        refreshToken: '',  // Empty - authentication via HttpOnly cookie only
        isAdmin: Boolean(token.roles.find((r: string) => r === 'admin')),
        isAuthenticated: true,
        isPasswordExpired: token.isPasswordExpired,
        roles: token.roles,
        fullName: token.fullName,
        employerId: token.employerId,
        contactId: token.contactId,
        employers: token.employers,
      };

      // Store only non-sensitive session metadata (roles, user info)
      // Do NOT store AuthToken or RefreshToken
      sessionStorage.setItem(storageRoles, JSON.stringify(token.roles));
      sessionStorage.setItem(storageAuthInformation, JSON.stringify({
        userId: token.userId,
        userName: token.userName,
        fullName: token.fullName,
        roles: token.roles,
        employers: token.employers,
        contactId: token.contactId,
        employerId: token.employerId,
        tenant: token.tenant,
        isPasswordExpired: token.isPasswordExpired,
        // Explicitly exclude authToken and refreshToken
      }));

      yield putResolve({ type: getType(userLoggedIn), payload: auth });
      yield putResolve({
        type: getType(loginActions.success),
        payload: token,
      });

      const startSession: boolean = yield call(session);
      yield putResolve({ type: getType(sessionStart), payload: startSession });

      return token;
    } else if (token.mfaRequired !== undefined && token.mfaRequired) {
      let newToken: TokenDto = JSON.parse(JSON.stringify(token)) as TokenDto;
      newToken.userName = creds.username;
      newToken.userPassword = creds.password;
      yield putResolve({
        type: getType(userLoggedIn),
        payload: {
          ...authInit,
          status: 'anonymous',
        },
      });
      yield put({ type: getType(loginActions.success), payload: newToken });
    } else {
      yield put({ type: getType(userLoginFail), payload: 'Login Failed' });
    }
  } catch (error) {
    yield put({ type: getType(userLoginFail), payload: 'Login Failed' });
  }

  return undefined;
}

function* loginAndRefreshToken(creds: ReturnType<typeof loginActions.request>) {
  const token: TokenDto = yield call(authorize, creds.payload);

  if (token !== undefined && token.isPasswordExpired) {
    sessionStorage.setItem(
      storageAuthInformation,
      JSON.stringify({
        ...JSON.parse(sessionStorage.getItem(storageAuthInformation) ?? 'null'),
        isPasswordExpired: token.isPasswordExpired,
      })
    );
    yield put({ type: getType(userLoginFail), payload: 'Password Update Required' });
  } else if (
    token !== undefined &&
    token.expiresIn !== undefined
  ) {
    const maxRetries = 3;
    const retryDelays = [10000, 30000, 60000];

    while (true) {
      yield call(delay, Math.max(token.expiresIn * 0.75, 10000));

      let succeeded = false;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const newToken: TokenDto = yield call(refreshAuth);

          if (newToken.isPasswordExpired) {
            sessionStorage.setItem(
              storageAuthInformation,
              JSON.stringify({
                ...JSON.parse(sessionStorage.getItem(storageAuthInformation) ?? 'null'),
                isPasswordExpired: true,
              })
            );
            yield put({
              type: getType(userLoggedIn),
              payload: {
                ...authInit,
                status: 'authenticated',
                isAuthenticated: true,
                isPasswordExpired: true,
                roles: token.roles,
                fullName: token.fullName,
                employerId: token.employerId,
                contactId: token.contactId,
                employers: token.employers,
              },
            });
            return;
          }

          if (newToken.authToken && newToken.authToken.length > 0) {
            const auth: AuthDto = {
              status: 'authenticated',
              authToken: '',
              refreshToken: '',
              isAdmin: Boolean(newToken.roles.find((r: string) => r === 'admin')),
              isAuthenticated: true,
              isPasswordExpired: token.isPasswordExpired,
              roles: newToken.roles,
              fullName: newToken.fullName,
              employerId: newToken.employerId,
              contactId: newToken.contactId,
              employers: newToken.employers,
            };

            sessionStorage.setItem(storageRoles, JSON.stringify(newToken.roles));
            sessionStorage.setItem(
              storageAuthInformation,
              JSON.stringify({
                userId: newToken.userId,
                userName: newToken.userName,
                fullName: newToken.fullName,
                roles: newToken.roles,
                employers: newToken.employers,
                contactId: newToken.contactId,
                employerId: newToken.employerId,
                tenant: newToken.tenant,
                isPasswordExpired: newToken.isPasswordExpired,
              })
            );

            yield put({ type: getType(userLoggedIn), payload: auth });
            succeeded = true;
            break;
          } else {
            yield put({ type: getType(userLoginFail), payload: 'Unauthorized' });
            succeeded = false;
            break;
          }
        } catch (error) {
          if (attempt < maxRetries - 1) {
            yield call(delay, retryDelays[attempt]);
          }
        }
      }

      if (!succeeded) {
        trackLogoutTrigger('login_saga_refresh_loop_exhausted', {
          originalExpiresIn: token.expiresIn?.toString() || 'unknown',
        });
        yield put({ type: getType(logoutActions.request) });
        return;
      }
    }
  }
}

function* handleLogout() {
  try {
    // Call backend logout endpoint to delete HttpOnly cookies
    yield call(logoutApi);
  } catch (err) {
    console.error('Logout API call failed:', err);
    // Continue with logout even if API call fails
  }

  // Set status to anonymous on logout
  const anonymousAuth: AuthDto = {
    ...authInit,
    status: 'anonymous',  // User logged out, now anonymous
  };
  yield put({ type: getType(userLoggedIn), payload: anonymousAuth });

  // Get tenant for redirect
  const tenant = localStorage.getItem(tenantId) ?? 'mha';

  // Clear session metadata
  sessionStorage.removeItem(storageRoles);
  sessionStorage.removeItem(storageAuthInformation);
  sessionStorage.clear();

  // Redirect to tenant-prefixed login page
  window.location.href = `/${tenant}/login`;
}

function* watchLoginRequest() {
  try {
    while (true) {
      const creds: ReturnType<typeof loginActions.request> = yield take(
        getType(loginActions.request)
      );
      yield race([
        take(getType(logoutActions.request)),
        call(loginAndRefreshToken, creds),
      ]);
    }
  } catch (err) {}
}

function* watchLogoutRequest() {
  yield takeLatest(getType(logoutActions.request), handleLogout);
}

export default [watchLoginRequest, watchLogoutRequest];
